import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, formatDateTime, formatQty } from "@/components/Shared";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ClipboardList, Plus, Save, Search, ChevronDown, ChevronRight, CheckCircle2, Wand2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export default function Inventory() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";

  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    const [b, w, h] = await Promise.all([
      api.get("/branches"),
      isAdmin ? api.get("/warehouses") : Promise.resolve({ data: [] }),
      api.get("/inventories"),
    ]);
    setBranches(b.data);
    setWarehouses(w.data || []);
    setHistory(h.data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  const warehouseName = (id) => warehouses.find((w) => w.id === id)?.name || id;
  const locationLabel = (inv) =>
    inv.scope === "warehouse" ? warehouseName(inv.location_id) : branchName(inv.location_id);

  return (
    <div data-testid="inventory-page">
      <PageHeader
        eyebrow="Skladová evidence"
        title="Inventury"
        description="Zápis fyzicky napočítaného množství proti očekávanému stavu. Admin: sklady i pobočky. Obsluha/Manažer: jen vlastní pobočka."
      />

      <div className="mb-6">
        <button
          data-testid="inv-new-btn"
          onClick={() => setOpen(true)}
          className="btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nová inventura
        </button>
      </div>

      <div className="card-cafe p-4 sm:p-6">
        <div className="label-eyebrow mb-3">Historie inventur</div>
        {history.length === 0 ? (
          <div className="text-sm text-[#57534E] py-8 text-center">Zatím žádné inventury.</div>
        ) : (
          <ul className="space-y-2" data-testid="inv-history">
            {history.map((inv) => {
              const op = expanded[inv.id];
              const diffColor = inv.total_difference > 0
                ? "#376A4D"
                : inv.total_difference < 0
                ? "#9E4747"
                : "#57534E";
              return (
                <li key={inv.id} className="border border-[#E5E1D8] rounded-md bg-white">
                  <button
                    onClick={() => setExpanded((e) => ({ ...e, [inv.id]: !op }))}
                    className="w-full flex items-center gap-3 p-3 hover:bg-[#F9F6F0] text-left"
                  >
                    {op ? <ChevronDown className="w-4 h-4 text-[#57534E]" /> : <ChevronRight className="w-4 h-4 text-[#57534E]" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate flex items-center gap-2">
                        <span className="text-xs uppercase tracking-wider text-[#57534E]">
                          {inv.scope === "warehouse" ? "Sklad" : "Pobočka"}
                        </span>
                        {locationLabel(inv)}
                        {inv.applied && (
                          <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-[#376A4D1A] text-[#376A4D] font-medium">
                            <CheckCircle2 className="w-3 h-3" /> aplikováno
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#57534E]">
                        {formatDateTime(inv.created_at)} · {inv.user_name || "—"} · {inv.lines?.length || 0} položek
                      </div>
                    </div>
                    <div className="text-right text-sm shrink-0">
                      <div className="text-xs text-[#57534E]">Rozdíl</div>
                      <div className="mono font-semibold" style={{ color: diffColor }}>
                        {inv.total_difference > 0 ? "+" : ""}{formatQty(inv.total_difference)}
                      </div>
                    </div>
                  </button>
                  {op && <InventoryDetail inv={inv} onApplied={load} />}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {open && (
        <NewInventoryDialog
          isAdmin={isAdmin}
          user={user}
          branches={branches}
          warehouses={warehouses}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function InventoryDetail({ inv, onApplied }) {
  const [names, setNames] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const path = inv.scope === "warehouse" ? "/materials" : "/products";
      const params = inv.scope === "branch" ? { include_deleted: true } : undefined;
      const { data } = await api.get(path, { params });
      const m = {};
      data.forEach((x) => (m[x.id] = { name: x.name, unit: x.unit || "ks" }));
      setNames(m);
    })();
  }, [inv.id, inv.scope]);

  const apply = async () => {
    const adjustments = inv.lines.filter((l) => Math.abs(l.difference) > 1e-9).length;
    if (!confirm(`Aplikovat inventuru? Vytvoří se ${adjustments} korekčních záznamů (chybějící → odpis, přebytek → kladná korekce).`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/inventories/${inv.id}/apply`);
      const s = data.summary;
      const parts = [];
      if (s.writeoffs) parts.push(`${s.writeoffs} odpisů`);
      if (s.dispatches) parts.push(`${s.dispatches} korekcí`);
      if (s.batches) parts.push(`${s.batches} skladových úprav`);
      toast.success(`Inventura aplikována — ${parts.join(", ") || "bez rozdílů"}`);
      onApplied?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-[#E5E1D8] p-3 bg-[#FBFAF6]">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[#57534E]">
              <th className="p-2 font-medium">Položka</th>
              <th className="p-2 font-medium text-right">Očekáváno</th>
              <th className="p-2 font-medium text-right">Napočítáno</th>
              <th className="p-2 font-medium text-right">Rozdíl</th>
              <th className="p-2 font-medium">Pozn.</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l, i) => {
              const info = names[l.item_id] || { name: l.item_id.slice(0, 8), unit: "" };
              const dc = l.difference > 0 ? "#376A4D" : l.difference < 0 ? "#9E4747" : "#57534E";
              return (
                <tr key={i} className="border-t border-[#E5E1D8]">
                  <td className="p-2 font-medium">{info.name}</td>
                  <td className="p-2 text-right mono">{formatQty(l.expected_quantity)} {info.unit}</td>
                  <td className="p-2 text-right mono">{formatQty(l.counted_quantity)} {info.unit}</td>
                  <td className="p-2 text-right mono font-medium" style={{ color: dc }}>
                    {l.difference > 0 ? "+" : ""}{formatQty(l.difference)}
                  </td>
                  <td className="p-2 text-xs text-[#57534E]">{l.note || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {inv.note && (
        <div className="mt-2 text-xs text-[#57534E]"><strong>Poznámka:</strong> {inv.note}</div>
      )}
      {!inv.applied && Math.abs(inv.total_difference) > 1e-9 && (
        <div className="mt-3 pt-3 border-t border-[#E5E1D8] flex items-center justify-end">
          <button
            data-testid={`inv-apply-${inv.id}`}
            onClick={apply}
            disabled={busy}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            title="Vytvoří korekční záznamy a srovná stavy s napočítaným množstvím"
          >
            <Wand2 className="w-4 h-4" />
            {busy ? "Aplikuji…" : "Aplikovat inventuru"}
          </button>
        </div>
      )}
      {inv.applied && (
        <div className="mt-3 pt-3 border-t border-[#E5E1D8] text-xs text-[#376A4D] inline-flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Aplikováno {inv.applied_at ? `· ${formatDateTime(inv.applied_at)}` : ""}
          {inv.apply_summary && (
            <span className="text-[#57534E]">
              · {inv.apply_summary.writeoffs || 0} odpisů, {inv.apply_summary.dispatches || 0} korekcí, {inv.apply_summary.batches || 0} skladových úprav
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function NewInventoryDialog({ isAdmin, user, branches, warehouses, onClose, onSaved }) {
  const defaultBranch = !isAdmin ? user.branch_id : (branches[0]?.id || "");
  const [scope, setScope] = useState("branch");
  const [locationId, setLocationId] = useState(defaultBranch);
  const [items, setItems] = useState([]); // {item_id, name, unit, expected_quantity, counted_quantity}
  const [search, setSearch] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const loadExpected = async (s, lid) => {
    if (!lid) { setItems([]); return; }
    try {
      const { data } = await api.get("/inventories/expected", { params: { scope: s, location_id: lid } });
      setItems(data.map((x) => ({ ...x, counted_quantity: "", note: "" })));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba načtení");
    }
  };

  useEffect(() => {
    loadExpected(scope, locationId);
    // eslint-disable-next-line
  }, [scope, locationId]);

  const updateLine = (id, patch) => {
    setItems((arr) => arr.map((x) => (x.item_id === id ? { ...x, ...patch } : x)));
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((x) => x.name.toLowerCase().includes(q));
  }, [items, search]);

  const totalDiff = useMemo(() => {
    return items.reduce((s, x) => {
      const c = parseFloat(x.counted_quantity);
      if (Number.isNaN(c)) return s;
      return s + (c - Number(x.expected_quantity));
    }, 0);
  }, [items]);

  const save = async () => {
    const lines = items
      .filter((x) => x.counted_quantity !== "" && !Number.isNaN(parseFloat(x.counted_quantity)))
      .map((x) => ({
        item_id: x.item_id,
        counted_quantity: parseFloat(x.counted_quantity),
        note: x.note || "",
      }));
    if (lines.length === 0) {
      toast.error("Vyplňte alespoň jednu položku");
      return;
    }
    setBusy(true);
    try {
      await api.post("/inventories", { scope, location_id: locationId, lines, note });
      toast.success(`Inventura uložena (${lines.length} položek)`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl bg-white border-[#E5E1D8]" data-testid="inv-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <ClipboardList className="w-5 h-5" /> Nová inventura
          </DialogTitle>
          <DialogDescription>
            Vyplňte napočítaný stav (jen položky, které jste počítali). Vynechané položky se neevidují.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-3">
          {isAdmin && (
            <label className="flex-1 min-w-[160px]">
              <div className="text-xs text-[#57534E] mb-1">Typ</div>
              <select
                data-testid="inv-scope"
                className="input-cafe w-full"
                value={scope}
                onChange={(e) => {
                  const newScope = e.target.value;
                  setScope(newScope);
                  setLocationId(newScope === "warehouse" ? (warehouses[0]?.id || "") : (branches[0]?.id || ""));
                }}
              >
                <option value="branch">Pobočka</option>
                <option value="warehouse">Sklad</option>
              </select>
            </label>
          )}
          <label className="flex-1 min-w-[200px]">
            <div className="text-xs text-[#57534E] mb-1">{scope === "warehouse" ? "Sklad" : "Pobočka"}</div>
            <select
              data-testid="inv-location"
              className="input-cafe w-full"
              value={locationId}
              disabled={!isAdmin && scope === "branch"}
              onChange={(e) => setLocationId(e.target.value)}
            >
              {(scope === "warehouse" ? warehouses : branches).map((x) => (
                <option key={x.id} value={x.id}>{x.name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" />
          <input
            data-testid="inv-search"
            className="input-cafe w-full pl-9"
            placeholder="Hledat položku…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="border border-[#E5E1D8] rounded-md max-h-[50vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#F9F6F0] z-10">
              <tr className="text-left text-xs uppercase tracking-wider text-[#57534E]">
                <th className="p-2 font-medium">Položka</th>
                <th className="p-2 font-medium text-right">Očekáváno</th>
                <th className="p-2 font-medium text-right">Napočítáno</th>
                <th className="p-2 font-medium text-right">Rozdíl</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={4} className="p-6 text-center text-[#57534E]">Bez výsledků.</td></tr>
              ) : filtered.map((x) => {
                const counted = parseFloat(x.counted_quantity);
                const diff = Number.isNaN(counted) ? null : counted - Number(x.expected_quantity);
                const dc = diff === null ? "#57534E" : (diff > 0 ? "#376A4D" : diff < 0 ? "#9E4747" : "#57534E");
                return (
                  <tr key={x.item_id} className="border-t border-[#E5E1D8]">
                    <td className="p-2">
                      <div className="font-medium">{x.name}</div>
                      {x.unit && <div className="text-xs text-[#57534E]">{x.unit}</div>}
                    </td>
                    <td className="p-2 text-right mono text-[#57534E]">{formatQty(x.expected_quantity)}</td>
                    <td className="p-2 text-right">
                      <input
                        data-testid={`inv-count-${x.item_id}`}
                        type="number"
                        inputMode="decimal"
                        step="0.001"
                        min="0"
                        className="input-cafe py-1 w-24 text-right text-sm"
                        value={x.counted_quantity}
                        onChange={(e) => updateLine(x.item_id, { counted_quantity: e.target.value })}
                      />
                    </td>
                    <td className="p-2 text-right mono" style={{ color: dc }}>
                      {diff === null ? "—" : (diff > 0 ? `+${formatQty(diff)}` : formatQty(diff))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <label className="block">
          <div className="text-xs text-[#57534E] mb-1">Poznámka (nepovinné)</div>
          <input
            data-testid="inv-note"
            className="input-cafe w-full"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="např. inventura k 30.6."
          />
        </label>

        <div className="text-sm flex justify-between border-t border-[#E5E1D8] pt-3">
          <span className="text-[#57534E]">Celkový rozdíl</span>
          <span
            className="mono font-semibold"
            style={{ color: totalDiff > 0 ? "#376A4D" : totalDiff < 0 ? "#9E4747" : "#57534E" }}
          >
            {totalDiff > 0 ? "+" : ""}{formatQty(totalDiff)}
          </span>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]">
            Zrušit
          </button>
          <button
            data-testid="inv-save-btn"
            onClick={save}
            disabled={busy}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {busy ? "Ukládám…" : "Uložit inventuru"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
